import { memo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { css, Interpolation, Theme } from '@emotion/react';
import { theme } from '@frontend/theme';
import { EVENT_Z_INDICES } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { CalendarEvent, CalendarEventType, isCalendarEventType } from '../../types';
import { CalendarDragStateContext, defaultCalendarDragState } from '../calendar-drag-state-context';
import { EventCardSwitch } from './event-card-switch';
import { EventShell } from './event-shell';

const EVENT_CSS_MAP: Record<CalendarEventType, Interpolation<Theme>> = {
  appointment: css({ zIndex: EVENT_Z_INDICES.appointment, margin: '1px 2px 1px 6px' }),
  scheduleDefinition: css({ zIndex: EVENT_Z_INDICES.scheduleDefinition, margin: '0 0 1px 1px' }),
  availabilitySlot: css({ zIndex: EVENT_Z_INDICES.availabilitySlot, margin: '0 0 1px 0' }),
  availableBlock: css({ zIndex: EVENT_Z_INDICES.availableBlock }),
  cancelledAppointment: css({ zIndex: EVENT_Z_INDICES.cancelledAppointment, margin: '1px 2px 1px 6px' }),
  exception: css({ zIndex: EVENT_Z_INDICES.exception }),
  scheduleRequest: css({ zIndex: EVENT_Z_INDICES.scheduleRequest, margin: '1px 2px 1px 6px' }),
  unavailableBlock: css({ zIndex: EVENT_Z_INDICES.unavailableBlock }),
  nonInteractiveSlot: css({ zIndex: EVENT_Z_INDICES.nonInteractiveSlot }),
  slotsStartLabel: css({ zIndex: EVENT_Z_INDICES.slotsStartLabel }),
};

const SELECTABLE_EVENT_TYPES = new Set<CalendarEventType>(['appointment', 'scheduleRequest', 'cancelledAppointment']);

interface Props {
  eventId: string;
  columnId?: string;
  style?: Interpolation<Theme>;
}

const EventRendererComponent = ({ eventId, columnId, style }: Props) => {
  const eventInfo = useCalendarGridStore((s) => s.eventsById[eventId]);
  const selectedEventId = useCalendarGridStore((s) => s.selectedEventId);
  const setSelectedEventId = useCalendarGridStore((s) => s.setSelectedEventId);
  const onEventSelect = useCalendarGridStore((s) => s.onEventSelect);
  const isEventDragEnabled = useCalendarGridStore((s) => s.isEventDragEnabled);
  const isEventDraggableRef = useCalendarGridStore((s) => s.isEventDraggableRef);
  const renderEventCardRef = useCalendarGridStore((s) => s.renderEventCardRef);
  const dragStyles = useCalendarGridStore((s) => s.dragStyles);
  const groupId = useCalendarGridStore((s) => {
    return columnId ? s.groupIds.find((gId) => s.groupsById[gId]?.columnIds.includes(columnId)) ?? '' : '';
  });

  // canDrag is false when the predicate explicitly rejects this event
  const canDrag = isEventDragEnabled && (eventInfo ? isEventDraggableRef.current?.(eventInfo) ?? true : false);

  const { setNodeRef, attributes, listeners, transform, isDragging } = useDraggable({
    id: eventId,
    data: { columnId, groupId },
    disabled: !canDrag,
  });

  if (!eventInfo || !isCalendarEventType(eventInfo.eventType)) return null;

  const isSelectable = SELECTABLE_EVENT_TYPES.has(eventInfo.eventType);
  const isSelected = isSelectable && selectedEventId === eventInfo.id;

  const eventCss = EVENT_CSS_MAP[eventInfo.eventType];
  const selectedCss = isSelected
    ? css({ outline: `2px solid ${theme.colors.primary50}`, outlineOffset: 2 })
    : undefined;

  // only transform must be inline — it is a dynamic dnd-kit value
  const dragTransformStyle =
    canDrag && !isDragging && transform
      ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
      : undefined;

  const dragCss = canDrag ? css({ touchAction: 'none', cursor: 'grab' }) : undefined;

  // eventCardOverlay applied via Emotion css; fall back to opacity:0 when not provided
  const resolvedEventCardOverlay =
    typeof dragStyles.eventCardOverlay === 'function'
      ? (dragStyles.eventCardOverlay as (e: CalendarEvent) => Interpolation<Theme>)(eventInfo)
      : dragStyles.eventCardOverlay;
  const eventCardOverlayCss = canDrag && isDragging ? resolvedEventCardOverlay ?? css({ opacity: 0 }) : undefined;

  const handleSelect = isSelectable
    ? () => {
        setSelectedEventId(eventInfo.id);
        onEventSelect?.(eventInfo.id);
      }
    : undefined;

  return (
    <CalendarDragStateContext.Provider
      value={{
        ...defaultCalendarDragState,
        isEventCardOverlay: isDragging,
        eventCardOverlayStyle: resolvedEventCardOverlay,
      }}
    >
      <EventShell
        ref={canDrag ? setNodeRef : undefined}
        id={eventInfo.id}
        startTime={eventInfo.startTime}
        duration={eventInfo.duration}
        style={dragTransformStyle}
        css={[eventCss, style, selectedCss, dragCss, eventCardOverlayCss]}
        data-dragging={isDragging ? 'true' : undefined}
        isSelectable={isSelectable}
        isSelected={isSelected}
        onSelect={handleSelect}
        {...(canDrag ? { ...attributes, ...listeners } : {})}
      >
        {renderEventCardRef?.current && typeof renderEventCardRef.current === 'function' ? (
          renderEventCardRef.current(eventInfo)
        ) : (
          <EventCardSwitch event={eventInfo} />
        )}
      </EventShell>
    </CalendarDragStateContext.Provider>
  );
};

export const EventRenderer = memo(EventRendererComponent);
